const DEFAULT_TIMEOUT_MS = 110_000;

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    readonly code?: string | null,
    readonly retryAfter?: string | null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type RequestJsonOptions<T> = RequestInit & {
  timeoutMs?: number;
  validate: (value: unknown) => value is T;
};

export async function requestJson<T>(
  url: string,
  { timeoutMs = DEFAULT_TIMEOUT_MS, validate, signal, ...init }: RequestJsonOptions<T>,
): Promise<T> {
  const controller = new AbortController();
  let timedOut = false;
  const abortFromCaller = () => controller.abort(signal?.reason);
  if (signal?.aborted) abortFromCaller();
  else signal?.addEventListener("abort", abortFromCaller, { once: true });

  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    const response = await fetch(url, { ...init, signal: controller.signal });
    const data = parseJson(await response.text());

    if (!response.ok) {
      const serverError = readServerError(data);
      throw new ApiError(
        serverError?.message ?? `The server rejected the request (${response.status}).`,
        response.status,
        serverError?.code,
        response.headers.get("Retry-After"),
      );
    }
    if (!validate(data)) {
      throw new ApiError("The server returned an invalid response.", response.status);
    }
    return data;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (timedOut) throw new ApiError("The request timed out. Try again.");
    if (signal?.aborted) {
      throw signal.reason instanceof Error
        ? signal.reason
        : new DOMException("Aborted", "AbortError");
    }
    throw new ApiError("The server could not be reached. Try again.");
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener("abort", abortFromCaller);
  }
}

function parseJson(body: string): unknown {
  if (!body) return null;
  try {
    return JSON.parse(body);
  } catch {
    throw new ApiError("The server returned invalid JSON.");
  }
}

function readServerError(data: unknown): { message: string; code?: string | null } | null {
  if (typeof data !== "object" || data === null || !("error" in data)) return null;
  const error = data.error;
  if (typeof error !== "object" || error === null || !("message" in error)) return null;
  if (typeof error.message !== "string") return null;
  return {
    message: error.message,
    code:
      "code" in error && (typeof error.code === "string" || error.code === null)
        ? error.code
        : undefined,
  };
}
